export const ASSET_GRID_COLUMNS = 5
export const ASSET_GRID_GAP = 16
/**
 * Neighbouring labels borrow from the same gutter, so each may take only what
 * leaves this much between two long names (HyperEVM beside Robinhood).
 */
export const ASSET_GRID_LABEL_GAP = 4
/** Shared so a cell's selection ring lands on the circle the caller passed it. */
export const ASSET_GRID_AVATAR_SIZE = 58

/**
 * The cells a capped grid offers, and how many the `+N` affordance stands for.
 *
 * - `mustShow` takes the **last** slot when the head would have hidden it, so a
 *   pick made behind the `+N` row comes back without adding a row or moving any
 *   other cell. Matched by identity.
 * - A non-positive `capacity` means no cap, so a caller still resolving its limit
 *   shows what it has rather than an empty grid.
 */
export function gridCells<T>(
  items: T[],
  capacity: number,
  mustShow?: T,
): { cells: T[]; overflowCount: number } {
  if (capacity <= 0 || items.length <= capacity) {
    return { cells: items, overflowCount: 0 }
  }
  const head = items.slice(0, capacity)
  const needsSlot =
    mustShow !== undefined &&
    !head.includes(mustShow) &&
    items.includes(mustShow)
  return {
    cells: needsSlot ? [...head.slice(0, -1), mustShow] : head,
    overflowCount: items.length - capacity,
  }
}

/** A non-positive `size` can't advance the cursor, so it stays one row. */
export function chunk<T>(items: T[], size: number): T[][] {
  if (size <= 0) {
    return items.length > 0 ? [items] : []
  }
  const rows: T[][] = []
  for (let i = 0; i < items.length; i += size) {
    rows.push(items.slice(i, i + size))
  }
  return rows
}
